/* eslint-disable @typescript-eslint/no-use-before-define */

import {QueryBuilderSelection} from '../types';
import {
  QueryEventEntry,
  QueryCohortEntry,
  CohortItem,
  QueryEntry,
  isQueryEventEntry,
  isInlineCohortItem,
} from 'common/widgets/query-event-selector/types';
import {ResourceType} from 'common/widgets/property-filter-menu/models/types';
import {dataGroupIDsEqual} from 'common/util/index';
import {hasUserFilters} from 'common/util/custom_events';
import PropertyFilter from 'common/widgets/property-filter-menu/models/property-filter';
import {
  CohortSegment,
  isCohortSegment,
  isCohortSegmentItem,
  isInlineCohortSegment,
  isUserSegment,
  Segment,
} from 'common/widgets/query-block/segment-menu/types';
import {CustomEventDefinition} from 'common/types/reports/custom-events';
import {reporter} from 'common/report/error-reporting';
import {PropertyItem} from 'common/widgets/query-block/properties-menu/types';

/**
 * Removes invalid entries for the selected data group and updates dataGroupId fields on the remaining entries
 */
export function setQueryDataGroup({
  query,
  dataGroupId,
  customEvents,
}: {
  query: QueryBuilderSelection;
  dataGroupId?: string;
  customEvents: CustomEventDefinition[];
}): QueryBuilderSelection {
  const sanitizedQuery = sanitizeQueryForDataGroup({query, dataGroupId, customEvents});
  return setQueryDataGroupProps({query: sanitizedQuery, dataGroupId});
}

/**
 * Returns true if the query has params that would be removed when switching to a given data group
 */
export function queryIsInvalidForDataGroup({
  query,
  dataGroupId,
  customEvents,
}: {
  query: QueryBuilderSelection;
  dataGroupId?: string;
  customEvents: CustomEventDefinition[];
}): boolean {
  return (
    query.events.some((queryEntry) => queryEntryIsInvalidForDataGroup({queryEntry, dataGroupId, customEvents})) ||
    query.exclusionSteps.some((queryEntry) =>
      queryEntryIsInvalidForDataGroup({queryEntry, dataGroupId, customEvents}),
    ) ||
    query.filters.some((f) => filterIsInvalidForDataGroup(f, dataGroupId)) ||
    groupByIsInvalidForDataGroup(query.groupBy, dataGroupId)
  );
}

export function isCustomEventWithUserFilters(
  eventEntry: QueryEventEntry,
  customEvents: CustomEventDefinition[],
): boolean {
  if (!eventEntry.event.custom) {
    return false;
  }
  if (!customEvents) {
    reporter.error(`customEvents argument missing`);
    return true;
  }

  const customEvent = customEvents.find((ce) => ce.id === eventEntry.event.id);
  return !customEvent || hasUserFilters(customEvent, customEvents);
}

export function cohortEntryIsInvalidForDataGroup(item: QueryCohortEntry | CohortSegment, dataGroupId?: string) {
  if (!isCohortSegmentItem(item)) {
    item = convertCohortItemToCohortSegment(item.event);
  }
  return !dataGroupIDsEqual(getCohortSegmentDataGroupId(item), dataGroupId);
}

function sanitizeQueryForDataGroup({
  query,
  dataGroupId,
  customEvents,
}: {
  query: QueryBuilderSelection;
  dataGroupId?: string;
  customEvents: CustomEventDefinition[];
}): QueryBuilderSelection {
  return {
    ...query,
    events: sanitizeEventsForDataGroup({queryEntries: query.events, dataGroupId, customEvents}),
    exclusionSteps: sanitizeEventsForDataGroup({queryEntries: query.exclusionSteps, dataGroupId, customEvents}),
    filters: query.filters.filter((f) => !filterIsInvalidForDataGroup(f, dataGroupId)),
    groupBy: sanitizeGroupByForDataGroup(query.groupBy, dataGroupId),
  };
}

function sanitizeEventsForDataGroup({
  queryEntries,
  dataGroupId,
  customEvents,
}: {
  queryEntries: QueryEntry[];
  dataGroupId?: string;
  customEvents: CustomEventDefinition[];
}): QueryEntry[] {
  // remove invalid event filters and property aggregations
  const sanitizedQueryEntries = queryEntries.map((entry) => {
    if (isQueryEventEntry(entry) || entry.type === ResourceType.User) {
      return {
        ...entry,
        filters: entry.filters.filter((f) => !filterIsInvalidForDataGroup(f, dataGroupId)),
        property: propertyIsInvalidForDataGroup(entry.property, dataGroupId) ? null : entry.property,
      };
    } else {
      return entry;
    }
  });
  return sanitizedQueryEntries.filter((queryEntry) => {
    return !queryEntryIsInvalidForDataGroup({queryEntry, dataGroupId, customEvents});
  });
}

function queryEntryIsInvalidForDataGroup({
  queryEntry,
  dataGroupId,
  customEvents,
}: {
  queryEntry: QueryEntry;
  dataGroupId?: string;
  customEvents: CustomEventDefinition[];
}): boolean {
  switch (queryEntry.type) {
    case ResourceType.Event:
      return (
        (dataGroupId && isCustomEventWithUserFilters(queryEntry, customEvents)) ||
        queryEntry.filters.some((f) => filterIsInvalidForDataGroup(f, dataGroupId)) ||
        (queryEntry.property && propertyIsInvalidForDataGroup(queryEntry.property, dataGroupId))
      );
    case ResourceType.User: {
      const isAllPeople = `$all_people` === queryEntry.event.value;
      return (
        (!isAllPeople && profileIsInvalidForDataGroup({profileDataGroupId: queryEntry.dataGroupId, dataGroupId})) ||
        queryEntry.filters.some((f) => filterIsInvalidForDataGroup(f, dataGroupId))
      );
    }
    case ResourceType.Cohort:
      return cohortEntryIsInvalidForDataGroup(queryEntry, dataGroupId);
    default:
      // Log unhandled cases and default to marking as invalid for non-null data group
      reporter.error(`Unexpected QueryEntry.type: ${JSON.stringify(queryEntry)}`);
      return !!dataGroupId;
  }
}

function filterIsInvalidForDataGroup(filter: Partial<PropertyFilter>, dataGroupId?: string): boolean {
  switch (filter.resourceType) {
    case ResourceType.User:
      return profileIsInvalidForDataGroup({profileDataGroupId: filter.dataGroupId, dataGroupId});
    case ResourceType.Cohort:
      return (filter.filterValue as CohortSegment[]).some((s) => cohortEntryIsInvalidForDataGroup(s, dataGroupId));
    case ResourceType.Event:
    case ResourceType.Other:
      return false;
  }
}

function isMultiGroupBy(groupBy: Segment[] | Segment[][]): groupBy is Segment[][] {
  return (
    Array.isArray(groupBy) && groupBy.length && Array.isArray(groupBy[0]) && !isCohortSegment(groupBy[0] as Segment)
  );
}

function sanitizeGroupByForDataGroup(groupBy: Segment[][] | Segment[], dataGroupId: string): Segment[][] {
  let segments: Segment[];
  if (groupBy.length === 0) {
    segments = [];
  } else if (isMultiGroupBy(groupBy)) {
    segments = groupBy.map((segmentsList) => segmentsList[0]);
  } else {
    segments = [groupBy[0]];
  }
  return segments.filter((s) => !segmentItemIsInvalidForDataGroup(s, dataGroupId)).map((s) => [s]);
}

function groupByIsInvalidForDataGroup(groupBy: Segment[] | Segment[][], dataGroupId: string) {
  let segments: Segment[];
  if (groupBy.length === 0) {
    segments = [];
  } else if (isMultiGroupBy(groupBy)) {
    segments = groupBy.map((segmentsList) => segmentsList[0]);
  } else {
    segments = [groupBy[0]];
  }
  return segments.some((segment) => segmentItemIsInvalidForDataGroup(segment, dataGroupId));
}

function segmentItemIsInvalidForDataGroup(segment: Segment, dataGroupId: string): boolean {
  if (isUserSegment(segment)) {
    return profileIsInvalidForDataGroup({profileDataGroupId: segment.user.dataGroupId, dataGroupId});
  } else if (isCohortSegment(segment)) {
    return segment.some((item) => cohortEntryIsInvalidForDataGroup(item, dataGroupId));
  } else {
    // EventSegment, OtherSegment
    return false;
  }
}

function setQueryDataGroupProps({
  query,
  dataGroupId,
}: {
  query: QueryBuilderSelection;
  dataGroupId?: string;
}): QueryBuilderSelection {
  return {
    ...query,
    events: query.events.map((entry) => ({...entry, dataGroupId})),
    exclusionSteps: query.exclusionSteps.map((entry) => ({...entry, dataGroupId})),
    dataGroupId,
  };
}

export function profileIsInvalidForDataGroup({
  profileDataGroupId,
  dataGroupId,
}: {
  profileDataGroupId: string;
  dataGroupId: string;
}) {
  if (dataGroupId) {
    // B2B analysis allows using all data groups' profile properties except for the empty "user" data group
    return !profileDataGroupId;
  } else {
    // User-level analysis can use all data groups' profile properties
    return false;
  }
}

function convertCohortItemToCohortSegment(item: CohortItem): CohortSegment {
  if (isInlineCohortItem(item)) {
    return {cohort: {...item.value.cohort, negated: false}};
  } else {
    return {
      cohort: {
        id: item.value,
        name: item.label,
        negated: false,
        ...(item.data_group_id ? {data_group_id: item.data_group_id} : null),
      },
    };
  }
}

function getCohortSegmentDataGroupId(segment: CohortSegment): string {
  if (isInlineCohortSegment(segment)) {
    return segment.cohort.raw_cohort.data_group_id;
  } else {
    return segment.cohort.data_group_id;
  }
}

function propertyIsInvalidForDataGroup(propertyItem?: PropertyItem, dataGroupId?: string): boolean {
  return (
    !!propertyItem &&
    propertyItem.resourceType === ResourceType.User &&
    profileIsInvalidForDataGroup({profileDataGroupId: propertyItem.dataGroupId, dataGroupId})
  );
}
